import {
  Material,
  AlwaysStencilFunc,
  EqualStencilFunc,
  GreaterDepth,
  KeepStencilOp,
  ReplaceStencilOp,
} from "three";
import { toRaw } from "vue";
import {
  ANNOTATION_DEPTH_TOLERANCE_M,
  GHOST_LAYER,
  GHOST_OPACITY,
} from "@/constants";

/**
 * Depth behaviour for annotations — measurement lines and points, area planes,
 * panel overlays.
 *
 * Annotations are occluded by the model: anything on the far side of the roof is
 * not drawn, and something crossing a ridge is drawn up to the ridge. That is
 * ordinary depth testing, but ordinary depth testing on its own is unusable on
 * this scene, for two reasons that both come down to annotations living *on* the
 * surface rather than above it:
 *
 *   - Measurement linework and area fills are built from points raycast onto the
 *     model, so on a planar roof face they are exactly coplanar with it, which
 *     is inside the depth buffer's noise at any near plane.
 *   - The model is photogrammetry. A measurement is a straight chord between two
 *     surface points; over a rippled roof the chord dips below the surface in the
 *     middle, so hard depth testing would eat holes out of the line.
 *
 * So every depth-tested annotation is shifted ANNOTATION_DEPTH_TOLERANCE_M
 * toward the camera in view space, for the depth test only — x, y and w are left
 * alone, so nothing moves on screen. Against the model that buys a tolerance
 * measured in metres, which is the unit the problem is actually in. Between
 * annotations it cancels out, because they all carry the same shift, so their
 * mutual occlusion stays exact per fragment.
 *
 * `RENDERING_ORDER` still decides draw order between element types, and so
 * decides which wins where two are exactly coplanar — the later draw passes the
 * default LESS_EQUAL test.
 */

// Appended to the end of the vertex shader, after gl_Position is final.
//
// Perspective: clip.w is the view-space distance, so the shifted vertex has
// w' = w - t and z' = z + projectionMatrix[2][2] * t. Orthographic: w is 1 and
// only z moves. Either way the depth we want is z'/w', and writing
// (z'/w') * w means it survives the perspective divide by the *original* w —
// which is what keeps the annotation in the same place on screen.
const depthToleranceGlsl = (bias) => /* glsl */ `
  {
    float annotationDepthBias = ${bias.toFixed(4)};
    float annotationZScale = projectionMatrix[2][2];
    bool annotationIsPerspective = projectionMatrix[2][3] != 0.0;

    float annotationBiasedZ = gl_Position.z + annotationZScale * annotationDepthBias;
    float annotationBiasedW = annotationIsPerspective
      ? max(gl_Position.w - annotationDepthBias, 1e-4)
      : gl_Position.w;

    gl_Position.z = (annotationBiasedZ / annotationBiasedW) * gl_Position.w;
  }
`;

function injectDepthTolerance(shader, glsl) {
  shader.vertexShader = shader.vertexShader.replace(/\}\s*$/, `${glsl}\n}`);
}

// For each material carrying the tolerance, the hook and cache key it had
// *before* we wrapped it. Kept so that marking an object again — which happens
// legitimately (a closed outline re-marked as a panel outline) — rebuilds the
// wrapper around the original hook instead of wrapping our own wrapper and
// stacking the bias.
const originalHooks = new WeakMap();

/**
 * Adds the tolerance to a material *on top of* whatever hook it already has.
 *
 * It must chain, never replace. InstancedMesh2 makes instancing work by wrapping
 * onBeforeCompile when the mesh is constructed — its wrapper swaps three's
 * per-instance attribute for a matrix texture — and an annotation is marked after
 * construction. Assigning over that wrapper stripped every instance of its
 * transform, which is what made area corners and midpoints vanish.
 *
 * The cache key has to change with it. three reuses a compiled program for any
 * material whose key matches, and by default that key is the source text of
 * onBeforeCompile — which after wrapping is this wrapper's source for everyone.
 * So the key is rebuilt from what the *previous* hook would have produced, plus a
 * suffix, keeping materials apart that differ only in the hook underneath.
 */
function applyDepthTolerance(material, bias) {
  let original = originalHooks.get(material);

  if (!original) {
    const key = material.customProgramCacheKey;
    original = {
      hook: material.onBeforeCompile,
      key,
      usesDefaultKey: key === Material.prototype.customProgramCacheKey,
      hookSource: material.onBeforeCompile.toString(),
      bias: null,
    };
    originalHooks.set(material, original);
  }

  if (original.bias === bias) return;
  original.bias = bias;

  const glsl = depthToleranceGlsl(bias);
  // The bias is baked into the source, so it has to be in the key too, or two
  // materials that differ only in bias would share one compiled program.
  const suffix = `|annotation-depth-tolerance:${bias}`;

  material.onBeforeCompile = function (shader, renderer) {
    original.hook.call(this, shader, renderer);
    injectDepthTolerance(shader, glsl);
  };

  material.customProgramCacheKey = function () {
    const base = original.usesDefaultKey
      ? original.hookSource
      : original.key.call(this);
    return base + suffix;
  };

  material.needsUpdate = true;
}

/**
 * Gives an object the annotation depth behaviour described above.
 *
 * @param {THREE.Object3D} object - the object; its current children are included,
 *   so add children before calling.
 * @param {number} [options.renderOrder] - rung on the `RENDERING_ORDER` ladder.
 * @param {boolean} [options.occludes=true] - pass false for anything that is
 *   not a thing standing on the roof:
 *
 *     - cursor and affordance chrome (the zoom pip, a panel's trash icon);
 *     - the measurement's x/y projection lines, which are a readout;
 *     - area fills, which are flat approximations of a surface that is not flat.
 *       Depth-testing those per fragment is what made roof detail near the
 *       tolerance flicker, so they are culled whole instead — see
 *       `updateAnnotationOcclusion` in `@/modules/measurement`.
 *
 *   These neither test nor write depth, so the depth buffer never hides them and
 *   they never stamp depth over anything else.
 * @param {number} [options.depthBias=ANNOTATION_DEPTH_TOLERANCE_M] - how far
 *   toward the camera this object is shifted for the depth test. Everything on
 *   the same bias is compared on its true depth, so a larger bias is how one
 *   kind of annotation is made to win against another it touches — see
 *   MARKER_DEPTH_BIAS_M.
 * @param {"line"|"fill"} [options.ghost="line"] - how the object takes part in
 *   the faint pass. "fill" is for a measurement area's body: depth-tested, but
 *   it neither writes depth (it would fight its own outline) nor marks the
 *   stencil, and its hidden part is drawn faintly only where no line or marker
 *   footprint is — see enrollOccludedFill.
 * @returns {THREE.Object3D} the object, for chaining.
 */
export function markAsAnnotation(
  object,
  {
    renderOrder,
    occludes = true,
    depthBias = ANNOTATION_DEPTH_TOLERANCE_M,
    ghost = "line",
  } = {}
) {
  setDepthBehaviour(object, occludes, depthBias, ghost !== "fill");
  if (renderOrder !== undefined) object.renderOrder = renderOrder;

  // Everything depth-tested has its hidden portions drawn faintly, from the
  // moment it exists — a line being drawn or dragged under a panel array is just
  // as worth seeing as a finished one. Re-marking without depth (a closed line
  // reused as a panel outline) takes it back out.
  object.traverse((child) => {
    if (!child.material) return;
    if (!occludes) unenrollOccludedGhost(child);
    else if (ghost === "fill") enrollOccludedFill(child);
    else enrollOccludedGhost(child);
  });

  return object;
}

function setDepthBehaviour(object, occludes, depthBias, writesDepth = true) {
  object.traverse((child) => {
    for (const material of materialsOf(child)) {
      material.depthTest = occludes;
      material.depthWrite = occludes && writesDepth;

      if (occludes) applyDepthTolerance(material, depthBias);
    }
  });
}

function materialsOf(object) {
  if (!object.material) return [];
  return Array.isArray(object.material) ? object.material : [object.material];
}

/*
 * Faint drawing of the hidden parts of lines and points.
 *
 * A line or point that is only partly behind the model is drawn twice. The
 * normal pass is unchanged: it depth-tests with the tolerance above, so it draws
 * what is in view and loses the rest. A later pass in renderAnnotatedFrame
 * redraws the same objects with the depth test inverted (GREATER) at reduced
 * opacity, which fills in exactly the fragments the first pass lost — the same
 * shader and the same bias compute the same depth, so the two passes split every
 * fragment between them with no gap and no overlap.
 *
 * On its own the inverted pass would also draw a line's hidden segment over any
 * annotation standing in front of it — most visibly, faintly across the line's
 * own corner marker. So the normal pass sets one stencil bit wherever a
 * participating line or point actually drew, and the faint pass only draws where
 * that bit is clear: hidden portions show through the model, never through
 * another annotation. The bit is the top one so that the stencil values the solar
 * inner plane writes for itself (its area index, a small number) never reach it.
 *
 * Area fills are kept out from under every participating line and point. An
 * area's outline runs along the edge of its own fill, so half of the line's width
 * would sit on the fill and half on the roof — in nearly the same green as the
 * line. Faint, that half either vanished (the stretch looked half as thick) or,
 * made stronger, piled onto the fill and read as a solid core inside a faint
 * band; no single opacity fixes two different backgrounds. So before the normal
 * render, a footprint pass marks a second stencil bit across every participant's
 * full on-screen shape, hidden parts included, and fills skip those pixels. A
 * faint outline then sits on the roof across its whole width, exactly like a
 * distance line. Solid lines and markers are opaque, so the fill missing under
 * them changes nothing visible.
 */
const SOLID_STENCIL_BIT = 0x80;
const FOOTPRINT_STENCIL_BIT = 0x40;

// Objects enrolled in the faint pass. A WeakSet, and the pass finds them by
// walking the scene, so an enrolled object that was never added yet — a line is
// marked when it is built and added to the scene afterwards — is simply waiting,
// and one that is removed and dropped costs nothing to forget.
const ghostObjects = new WeakSet();

/**
 * Enrols a line or point in the faint pass, with its faint portions on.
 * Idempotent. markAsAnnotation calls this for every depth-tested annotation;
 * setOccludedGhostVisible then turns the faint portions off for a finished area
 * or measurement that is hidden as a whole.
 */
export function enrollOccludedGhost(object) {
  if (!object || ghostObjects.has(object)) return;

  // Only depth-tested annotations — the ones markAsAnnotation gave the tolerance
  // to — can take part: the faint pass is the exact complement of *their* depth
  // test. Anything else (panels, the model) is refused rather than enrolled,
  // since it would stamp the stencil bit and suppress real faint stretches.
  const materials = materialsOf(object);
  if (!materials.length || !materials.every((m) => originalHooks.has(m))) return;

  ghostObjects.add(object);

  for (const material of materials) {
    material.stencilWrite = true;
    material.stencilFunc = AlwaysStencilFunc;
    material.stencilRef = SOLID_STENCIL_BIT;
    material.stencilFuncMask = 0xff;
    material.stencilWriteMask = SOLID_STENCIL_BIT;
    material.stencilFail = KeepStencilOp;
    material.stencilZFail = KeepStencilOp;
    material.stencilZPass = ReplaceStencilOp;
  }

  object.layers.enable(GHOST_LAYER);
}

// Measurement-area bodies in the faint pass. Kept apart from ghostObjects
// because a fill takes part differently: no footprint, no solid mark, and a
// different stencil test for its faint part.
const ghostFills = new WeakSet();

/**
 * Enrols an area's body in the faint pass, with its faint part on. Its stencil
 * is left as excludeLineFootprints set it: it never marks anything itself — a
 * translucent wash is not "solid", so faint lines still show across it.
 */
export function enrollOccludedFill(object) {
  if (!object || ghostFills.has(object)) return;

  const materials = materialsOf(object);
  if (!materials.length || !materials.every((m) => originalHooks.has(m))) return;

  ghostFills.add(object);
  object.layers.enable(GHOST_LAYER);
}

/** Takes an object back out of the faint pass. Idempotent. */
export function unenrollOccludedGhost(object) {
  if (!object) return;

  if (ghostFills.has(object)) {
    ghostFills.delete(object);
    object.layers.disable(GHOST_LAYER);
    return;
  }

  if (!ghostObjects.has(object)) return;
  ghostObjects.delete(object);

  object.layers.disable(GHOST_LAYER);
  for (const material of materialsOf(object)) material.stencilWrite = false;
}

/**
 * Turns the faint portions of an enrolled object on or off. Off is for an area or
 * measurement that is hidden as a whole: it should vanish, not show through the
 * building as a ghost.
 */
export function setOccludedGhostVisible(object, visible) {
  if (!object || !(ghostObjects.has(object) || ghostFills.has(object))) return;
  if (visible) object.layers.enable(GHOST_LAYER);
  else object.layers.disable(GHOST_LAYER);
}

// Selected measurements and areas (the owner objects, not their meshes). A
// selected item that is only partly hidden is shown at full strength — its
// hidden stretches and its labels are not faded — so the thing you are working
// on is never half-faded. Hidden as a whole still hides it.
const selectedOwners = new WeakSet();

export function setAnnotationSelected(owner, selected) {
  owner = toRaw(owner);
  if (!owner) return;
  if (selected) selectedOwners.add(owner);
  else selectedOwners.delete(owner);
}

export function isAnnotationSelected(owner) {
  return selectedOwners.has(toRaw(owner));
}

// Objects whose hidden parts the faint pass draws at full strength rather than
// GHOST_OPACITY: the lines, markers and body of a selected item.
const emphasizedObjects = new WeakSet();

export function setOccludedEmphasis(object, emphasized) {
  object = toRaw(object);
  if (!object) return;
  if (emphasized) emphasizedObjects.add(object);
  else emphasizedObjects.delete(object);
}

/**
 * Drops pick hits on annotations hidden as a whole — a measurement or area on
 * the far side of the roof. A raycast hits an object wherever it is, so without
 * this a hidden measurement could still be hovered, selected and dragged.
 *
 * Anything drawn, solid or faint, stays pickable. Hidden as a whole means
 * enrolled in the faint pass with the faint layer switched off by
 * setOccludedGhostVisible.
 */
export function dropHitsOnHiddenAnnotations(intersects) {
  return intersects.filter((hit) => {
    // Viewer state hands back Vue proxies, which are not keys in the WeakSets.
    const object = toRaw(hit.object);
    const enrolled = ghostObjects.has(object) || ghostFills.has(object);
    return !enrolled || object.layers.isEnabled(GHOST_LAYER);
  });
}

/**
 * Makes a fill skip every pixel inside a participating line's or point's
 * footprint (see the note on FOOTPRINT_STENCIL_BIT). Keeps whatever stencil value
 * the fill writes for itself, limited to the bits below both of ours.
 */
export function excludeLineFootprints(material) {
  const wasWriting = material.stencilWrite;

  material.stencilWrite = true;
  material.stencilFunc = EqualStencilFunc;
  material.stencilFuncMask = FOOTPRINT_STENCIL_BIT;

  if (wasWriting) {
    material.stencilRef &= FOOTPRINT_STENCIL_BIT - 1;
    material.stencilWriteMask = FOOTPRINT_STENCIL_BIT - 1;
  } else {
    material.stencilRef = 0;
    material.stencilWriteMask = 0;
  }
}

function collectParticipants(scene) {
  // traverseVisible skips hidden subtrees, so a hidden object and everything
  // under it is never touched. Only objects whose faint portions are on take
  // part: a measurement hidden as a whole must not cut a gap in a visible fill.
  const participants = [];
  const fills = [];
  scene.traverseVisible((object) => {
    if (!object.layers.isEnabled(GHOST_LAYER)) return;
    if (ghostObjects.has(object)) participants.push(object);
    else if (ghostFills.has(object)) fills.push(object);
  });
  return { participants, fills };
}

const SAVED_FIELDS = [
  "transparent",
  "colorWrite",
  "depthTest",
  "depthFunc",
  "depthWrite",
  "stencilFunc",
  "stencilRef",
  "stencilFuncMask",
  "stencilWriteMask",
  "stencilFail",
  "stencilZFail",
  "stencilZPass",
];

/**
 * Renders the participants alone, on the ghost layer, with their materials
 * temporarily reconfigured by `configure`, and puts everything back after.
 * Material depth, colour-write and stencil settings are plain GL state in
 * three.js, so none of this recompiles a shader.
 */
function renderParticipants(renderer, scene, camera, participants, configure) {
  const saved = new Map();
  for (const object of participants) {
    for (const material of materialsOf(object)) {
      if (saved.has(material)) continue;
      const opacity = material.uniforms?.opacity;
      const state = Object.fromEntries(SAVED_FIELDS.map((f) => [f, material[f]]));
      state.opacity = opacity ? opacity.value : material.opacity;
      saved.set(material, state);
      configure(material, opacity, object);
    }
  }

  // A Color background force-clears colour and depth on every renderer.render,
  // and a texture one re-draws the gradient — either would wipe the frame these
  // passes draw on.
  const background = scene.background;
  const cameraMask = camera.layers.mask;
  scene.background = null;
  camera.layers.set(GHOST_LAYER);

  try {
    renderer.render(scene, camera);
  } finally {
    camera.layers.mask = cameraMask;
    scene.background = background;

    for (const [material, state] of saved) {
      for (const f of SAVED_FIELDS) material[f] = state[f];
      if (material.uniforms?.opacity) material.uniforms.opacity.value = state.opacity;
      else material.opacity = state.opacity;
    }
  }
}

/**
 * Renders one frame: footprints, the scene, then the faint hidden portions.
 *
 * Owns the stencil buffer for the frame. Automatic stencil clearing is turned
 * off — a Color background force-clears on every renderer.render, which would
 * wipe the footprints before the scene is drawn — and the stencil is cleared
 * here instead, once, at the start of every frame. That also stops a second
 * render in the same frame (screenshot capture) inheriting the first's stencil.
 */
export function renderAnnotatedFrame(renderer, scene, camera) {
  renderer.autoClearStencil = false;
  // gl.clear honours the stencil write mask, which the last material drawn may
  // have left narrowed.
  renderer.state.buffers.stencil.setMask(0xff);
  renderer.clearStencil();

  const { participants, fills } = collectParticipants(scene);

  // 1. Footprints: stencil only — no colour, no depth — across each
  //    participant's whole shape, hidden parts included.
  if (participants.length) {
    renderParticipants(renderer, scene, camera, participants, (material) => {
      material.colorWrite = false;
      material.depthTest = false;
      material.depthWrite = false;
      material.stencilFunc = AlwaysStencilFunc;
      material.stencilRef = FOOTPRINT_STENCIL_BIT;
      material.stencilFuncMask = 0xff;
      material.stencilWriteMask = FOOTPRINT_STENCIL_BIT;
      material.stencilFail = KeepStencilOp;
      material.stencilZFail = KeepStencilOp;
      material.stencilZPass = ReplaceStencilOp;
    });
  }

  // 2. The scene, as always. Fills skip the footprints; participants mark the
  //    solid bit wherever they actually draw.
  renderer.render(scene, camera);

  // 3. The hidden portions, faintly: where the normal depth test lost
  //    (GREATER). A line or point skips pixels where another participant drew
  //    solid; a fill skips the footprints of lines and points, as it does in
  //    the normal render. Fills sit lower on the render-order ladder, so they
  //    are drawn first and the faint lines land on top of them.
  const faint = [...fills, ...participants];
  if (faint.length) {
    renderParticipants(renderer, scene, camera, faint, (material, opacity, object) => {
      const isFill = ghostFills.has(object);

      // Opacity only blends on a transparent material; every line and marker
      // is one already, but this keeps an opaque newcomer from drawing its
      // faint portion solid.
      material.transparent = true;
      material.depthFunc = GreaterDepth;
      material.depthWrite = false;
      material.stencilFunc = EqualStencilFunc;
      material.stencilRef = 0;
      material.stencilFuncMask = isFill ? FOOTPRINT_STENCIL_BIT : SOLID_STENCIL_BIT;
      material.stencilWriteMask = 0;
      material.stencilZPass = KeepStencilOp;

      const factor = emphasizedObjects.has(object) ? 1 : GHOST_OPACITY;
      if (opacity) opacity.value *= factor;
      else material.opacity *= factor;
    });
  }
}
